import { Injectable, inject, signal, computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { QUICK_ACTIONS, QuickActionFlow, CONTEXT_DEFAULT_PARAMS_BY_ENDPOINT } from '../constants';
import { VoiceIntent, VoiceMessageOption, SessionContextEntity } from '../models';
import { VoiceContextService } from './voice-context.service';
import { VoiceSessionService } from './voice-session.service';
import { VoiceClarificationService } from './voice-clarification.service';
import { VoiceService } from './voice.service';
import { VoiceTelemetryService } from './voice-telemetry.service';
import { VoiceCalendarChoiceService } from './voice-calendar-choice.service';
import { LAST_CALENDAR_HINT } from '../handlers/calendar-choice.util';
import { ProjectService } from '../../../modules/project-management/services/project.service';
import { TaskService } from '../../../modules/project-management/services/task.service';
import { TaskStatusService } from '../../../modules/project-management/services/task-status.service';
import { AuthService } from '../../auth/auth.service';
import { CalendarDataService } from '../../../modules/calendar/services/calendar-data.service';
import { CalendarPortion } from '../../../models/enums';

/** Forme brute d'une ligne `project_members` (jointure `profile:profiles(*)`), cf. ProjectVoiceHandler */
interface ProjectMemberRow {
  profile_id?: string;
  profile?: {
    id?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  };
}

/** Choix de projet mémorisé le temps de l'étape `project_pick` (pas affiché tel quel — cf. `#ask`) */
interface ProjectChoice {
  id: string;
  identifier: string;
  name: string;
}

/** Choix de tâche mémorisé le temps de l'étape `task` */
interface TaskChoice {
  id: string;
  title: string;
}

/**
 * Défaut de contexte conversationnel (`session.setContext`, cf.
 * `SessionVoiceHandler`) injecté dans une création guidée — cf.
 * `#collectContextDefaults`/`#executeFlow`. `paramKey` est le nom du
 * paramètre d'intent qu'il pré-remplit (ex. `assigneeName`), `label`/`value`
 * servent uniquement à l'affichage du récap de confirmation.
 */
interface ContextDefault {
  paramKey: string;
  label: string;
  value: string;
}

/**
 * Endpoint → paramètres de contexte (`ContextDefault.paramKey`) réellement
 * CONSOMMÉS par le handler cible — table déclarative consultée par
 * `#filterSupportedDefaults`, PAS par flow individuel : un futur endpoint de
 * création qui gagnerait le support de `groupName`/`releaseName`
 * (aujourd'hui lus par aucun handler) n'a qu'à étendre son entrée ici, sans
 * toucher au moteur de flow. Évite le récap "fausse promesse" (ex. afficher
 * "Sprint : X" pour une création dont le handler ignore silencieusement ce
 * champ) — `project.createProject` n'a volontairement AUCUNE entrée :
 * `ProjectVoiceHandler.handleCreateProject` ne lit ni assigné, ni échéance,
 * ni groupe, ni release. `project.createTask` ne liste que
 * `assigneeName`/`dueDate` : `handleCreateTask` ne lit pas encore de
 * groupe/release (pas de colonne `task_group_id`/`release_id` posée par ce
 * lot, cf. rapport de fin de tâche).
 */
// Table partagée avec `VoiceService` (chemin GPT) — cf. sa JSDoc.
const CONTEXT_DEFAULT_SUPPORTED_PARAMS = CONTEXT_DEFAULT_PARAMS_BY_ENDPOINT;

/**
 * État d'un flow guidé en cours. `step` est une union de string literals propre
 * à chaque `flow` (ex. `'project_confirm' | 'project_pick' | 'title'` pour
 * `createTask`) — volontairement non typée strictement ici (union de tous les
 * steps possibles, peu lisible) : la validité du couple (flow, step) est
 * garantie par construction (seules les méthodes privées de ce service
 * produisent des transitions), pas par le compilateur.
 */
export interface GuidedFlowState {
  flow: QuickActionFlow;
  step: string;
  answers: Record<string, unknown>;
}

/**
 * Moteur des flows guidés par boutons (MOKA-206 Phase 3, Lot F) : catalogue
 * d'actions rapides (créer une tâche, assigner, changer un statut...) menées
 * question par question (boutons ou texte libre selon l'étape), sans jamais
 * repasser par GPT — chaque flow se termine par un appel à
 * `VoiceService.executeIntent`, le point d'entrée unique d'exécution (Lot G).
 *
 * Gère aussi la résolution de la clarification "projet manquant" (Lot H,
 * `resolveClarification`) : la construction des options et la mémorisation de
 * l'intent en attente vivent sur `VoiceClarificationService` (service tiers,
 * sans dépendance vers celui-ci ni vers `VoiceService`) pour éviter un cycle
 * DI — voir la JSDoc de `VoiceClarificationService` pour le détail du
 * raisonnement. Ce service-ci se contente de lire `pendingClarification` et de
 * rejouer l'intent complété une fois le projet choisi.
 *
 * Annulation (règle transverse F) : taper du texte libre pendant qu'une étape
 * attend un clic bouton, ou qu'une clarification est en attente, annule
 * proprement (cf. `submitText`) — c'est `VoiceAssistantOverlayComponent` qui
 * orchestre l'enchaînement exact (voir sa JSDoc `onSendText`).
 */
@Injectable({ providedIn: 'root' })
export class VoiceGuidedFlowService {
  private voiceContext = inject(VoiceContextService);
  private voiceSession = inject(VoiceSessionService);
  private clarification = inject(VoiceClarificationService);
  private voiceService = inject(VoiceService);
  private projectService = inject(ProjectService);
  private taskService = inject(TaskService);
  private taskStatusService = inject(TaskStatusService);
  private auth = inject(AuthService);
  /** Retour terrain : vérification "calendrier actif" AVANT le flow `addToCalendar`, cf. `start()` */
  private calendarData = inject(CalendarDataService);
  private telemetry = inject(VoiceTelemetryService);
  private calendarChoice = inject(VoiceCalendarChoiceService);

  #activeFlow = signal<GuidedFlowState | null>(null);

  /** Flow guidé en cours (`null` si aucun) */
  activeFlow = computed(() => this.#activeFlow());

  /** Clarification "projet manquant" en attente (délègue à `VoiceClarificationService`, cf. JSDoc de classe) */
  pendingClarification = computed(() => this.clarification.pendingClarification());

  /**
   * Démarre un flow guidé : pousse la première question (avec ses boutons,
   * le cas échéant) dans le fil de conversation. Les flows qui portent sur un
   * projet (`createTask`/`assignTask`/`updateTaskStatus`/`deleteTask`, et
   * depuis MOKA-207 Lot 4 `projectSummary`/`commentTask`) pré-résolvent le
   * contexte de la page courante quand il existe, plutôt que de sauter
   * directement à la liste complète des projets. `myTasks`/`reserveRoom`/
   * `reportClaim` (même lot) ne portent PAS sur un projet — flows courts et
   * indépendants, cf. leurs sections dédiées plus bas.
   */
  async start(flow: QuickActionFlow): Promise<void> {
    if (!this.auth.profile) {
      this.#ask('Vous devez être connecté pour effectuer cette action.');
      return;
    }

    this.telemetry.track({ eventType: 'flow_started', endpoint: flow, inputMode: 'text' });
    this.voiceSession.suggestFlowTitle(this.#actionLabel(flow));

    // MOKA-207 Lot 3 : pré-remplissage des défauts de contexte conversationnel
    // (personne/sprint/release/échéance posés via `session.setContext`) pour
    // toute création (`flow` préfixé `create`) — générique par construction :
    // un futur flow `createX` en bénéficie automatiquement sans toucher à ce
    // switch, cf. `#collectContextDefaults`/`#executeFlow` pour la suite
    // (récap-confirmation avant exécution).
    const initialAnswers = this.#buildInitialAnswers(flow);

    switch (flow) {
      case 'createTask':
      case 'assignTask':
      case 'updateTaskStatus':
      case 'deleteTask':
      // MOKA-207 Lot 4 : `projectSummary` (résumé) et `commentTask` (choix de
      // la tâche à commenter) portent aussi sur un projet — même résolution
      // de contexte que les flows existants, cf. `#onProjectResolved` pour
      // la suite propre à chacun.
      case 'projectSummary':
      case 'commentTask':
        await this.#beginWithProjectStep(flow, initialAnswers);
        return;
      case 'createProject':
        this.#setState(flow, 'name', initialAnswers);
        this.#ask('Quel est le nom du projet ?');
        return;
      case 'addToCalendar':
        await this.#beginAddToCalendar(flow);
        return;
      // ─── MOKA-207 Phase 4 Lot 4 : nouveaux flows guidés ─────────────────
      case 'myTasks':
        this.#setState(flow, 'scope', {});
        this.#ask('Quelles tâches ?', [
          { id: 'today', label: 'Aujourd\'hui' },
          { id: 'overdue', label: 'En retard' },
          { id: 'all', label: 'Toutes' }
        ]);
        return;
      case 'reserveRoom':
        this.#setState(flow, 'room_when', {});
        this.#ask('Pour quel jour ?', [
          { id: 'today', label: 'Aujourd\'hui' },
          { id: 'tomorrow', label: 'Demain' }
        ]);
        return;
      case 'reportClaim':
        this.#setState(flow, 'claimDescription', {});
        this.#ask('Quel est le problème ?');
        return;
      default: {
        const exhaustive: never = flow;
        throw new Error(`Flow non supporté: ${exhaustive}`);
      }
    }
  }

  /**
   * Avance l'étape courante avec une réponse bouton : ajoute le message user
   * correspondant (`option.label`), puis enchaîne vers l'étape suivante ou
   * exécute l'intent final si c'était la dernière étape.
   */
  async submitOption(option: VoiceMessageOption): Promise<void> {
    const state = this.#activeFlow();
    if (!state) return;

    this.voiceSession.addMessage('user', option.label, 'text', undefined, false);

    switch (state.step) {
      case 'project_confirm':
        await this.#handleProjectConfirm(state, option);
        break;
      case 'project_pick':
        await this.#handleProjectPick(state, option);
        break;
      case 'task':
        await this.#handleTaskPick(state, option);
        break;
      case 'assignee':
        await this.#handleAssigneePick(state, option);
        break;
      case 'status':
        await this.#handleStatusPick(state, option);
        break;
      case 'calendar_pick':
        await this.#handleCalendarPick(state, option);
        break;
      case 'when':
        await this.#handleWhenPick(state, option);
        break;
      case 'portion':
        await this.#handlePortionPick(state, option);
        break;
      case 'confirm':
        await this.#handleDeleteConfirm(state, option);
        break;
      case 'scope':
        await this.#handleScopePick(state, option);
        break;
      case 'room_when':
        await this.#handleRoomWhenPick(state, option);
        break;
      case 'context_recap':
        await this.#handleContextRecapConfirm(state, option);
        break;
      default:
        break;
    }
  }

  /**
   * Consomme une réponse texte libre :
   * - aucun flow actif → `false` immédiatement (le composant appelant route
   *   normalement vers GPT) ; si une clarification Lot C était en attente,
   *   elle est annulée au passage (même règle d'annulation) ;
   * - flow actif mais étape à choix fermé (boutons) → `false`, ne consomme
   *   rien (annulation du flow laissée à l'appelant, cf. `cancel()`) ;
   * - flow actif et étape à texte libre (`title`/`name`/`comment`/
   *   `claimDescription`) → consomme la réponse, exécute, retourne `true`.
   */
  async submitText(text: string): Promise<boolean> {
    const state = this.#activeFlow();

    if (!state) {
      if (this.clarification.pendingClarification()) {
        this.clarification.clearPendingClarification();
      }
      return false;
    }

    const freeTextSteps = ['title', 'name', 'comment', 'claimDescription'];
    if (!freeTextSteps.includes(state.step)) {
      return false;
    }

    this.voiceSession.addMessage('user', text, 'text');
    const trimmed = text.trim();

    switch (state.step) {
      case 'title':
        await this.#executeFlow(state.flow, { ...state.answers, title: trimmed });
        break;
      case 'name':
        await this.#executeFlow(state.flow, { ...state.answers, name: trimmed });
        break;
      case 'comment':
        await this.#executeFlow(state.flow, { ...state.answers, comment: trimmed });
        break;
      case 'claimDescription':
        await this.#executeFlow(state.flow, { ...state.answers, description: trimmed });
        break;
    }

    return true;
  }

  /** Annule proprement le flow en cours (no-op si aucun flow actif) */
  cancel(): void {
    // MOKA-208 : mesurer où les parcours décrochent. On ne trace que si un flow
    // était réellement engagé — `cancel()` est aussi appelé en garde d'entrée.
    const abandoned = this.#activeFlow();
    if (abandoned) {
      this.telemetry.track({
        eventType: 'flow_abandoned',
        endpoint: abandoned.flow,
        inputMode: 'text',
        failureReason: `etape_${abandoned.step}`
      });
    }
    this.#activeFlow.set(null);
  }

  /**
   * Résout la clarification "projet manquant" (Lot H) : fusionne le projet
   * choisi (`option.id`, un identifiant de projet) dans les `params` de
   * l'intent mémorisé, puis l'exécute directement via
   * `VoiceService.executeIntent` — sans jamais repasser par GPT.
   */
  async resolveClarification(option: VoiceMessageOption): Promise<void> {
    const pending = this.clarification.pendingClarification();
    if (!pending) return;

    this.voiceSession.addMessage('user', option.label, 'text', undefined, false);

    const completedIntent: VoiceIntent = {
      ...pending.intent,
      params: { ...pending.intent.params, projectName: option.id }
    };

    this.clarification.clearPendingClarification();
    await this.voiceService.executeIntent(completedIntent, 'text');
  }

  // ─── Étape « projet » ─────────────────────────────────────────────────

  /**
   * MOKA-206 Lot F : même règle de préséance que les chips de contexte
   * (`VoiceContextChipsComponent`) — `entities.lastProject` (conversation)
   * prime toujours sur `voiceContext` (page). Le contexte de page n'est
   * proposé que s'il n'a pas été écarté par l'utilisateur
   * (`voiceSession.ignoredPageContext()`) ; dans ce cas comme dans celui où
   * aucun contexte n'existe, on saute directement à la liste complète.
   * `answers` (MOKA-207 Lot 3) porte les défauts de contexte éventuellement
   * pré-remplis par `start()` (`#buildInitialAnswers`) — simple relais vers
   * chaque étape suivante, aucune logique dessus ici.
   */
  async #beginWithProjectStep(flow: QuickActionFlow, answers: Record<string, unknown> = {}): Promise<void> {
    const lastProject = this.voiceSession.entities().lastProject;
    if (lastProject) {
      this.#setState(flow, 'project_confirm', answers);
      this.#ask(`Dans le projet ${lastProject.name} — OK ?`, [
        { id: 'yes', label: 'Oui' },
        { id: 'other', label: 'Choisir un autre' }
      ]);
      return;
    }

    const currentId = this.voiceContext.currentProjectId();
    const currentName = this.voiceContext.currentProjectName();
    const pageIgnored = this.voiceSession.ignoredPageContext().has('project');

    if (currentId && currentName && !pageIgnored) {
      this.#setState(flow, 'project_confirm', answers);
      this.#ask(`Dans le projet ${currentName} (ouvert) — OK ?`, [
        { id: 'yes', label: 'Oui' },
        { id: 'other', label: 'Choisir un autre' }
      ]);
      return;
    }

    await this.#askProjectList(flow, answers);
  }

  async #askProjectList(flow: QuickActionFlow, answers: Record<string, unknown>): Promise<void> {
    const response = await firstValueFrom(this.projectService.getAllProjects());
    const choices: ProjectChoice[] = response.projects
      .filter((p): p is typeof p & { id: string } => !!p.id)
      .map(p => ({ id: p.id, identifier: p.identifier, name: p.name }));

    if (choices.length === 0) {
      this.#ask('Aucun projet accessible.');
      this.cancel();
      return;
    }

    this.#setState(flow, 'project_pick', { ...answers, projectChoices: choices });
    this.#ask('Quel projet ?', choices.map(c => ({ id: c.identifier, label: c.name })));
  }

  async #handleProjectConfirm(state: GuidedFlowState, option: VoiceMessageOption): Promise<void> {
    if (option.id === 'yes') {
      // Re-dérive la même source que celle annoncée par `#beginWithProjectStep`
      // (conversation en priorité) plutôt que de la mémoriser dans `state` —
      // le contexte est lu à nouveau, cohérent par construction avec la
      // question qui vient d'être posée.
      const lastProject = this.voiceSession.entities().lastProject;
      if (lastProject) {
        await this.#onProjectResolved(
          state.flow,
          { projectId: lastProject.id, projectName: lastProject.name, projectIdentifier: lastProject.identifier },
          state.answers
        );
        return;
      }

      const projectId = this.voiceContext.currentProjectId();
      const projectName = this.voiceContext.currentProjectName() ?? undefined;
      if (!projectId) {
        // Défense : le contexte a changé entre l'affichage de la question et la réponse.
        await this.#askProjectList(state.flow, state.answers);
        return;
      }
      await this.#onProjectResolved(state.flow, { projectId, projectName }, state.answers);
      return;
    }

    await this.#askProjectList(state.flow, state.answers);
  }

  async #handleProjectPick(state: GuidedFlowState, option: VoiceMessageOption): Promise<void> {
    const choices = (state.answers['projectChoices'] as ProjectChoice[] | undefined) ?? [];
    const choice = choices.find(c => c.identifier === option.id);

    if (!choice) {
      this.#ask('Projet introuvable, réessayez.');
      this.cancel();
      return;
    }

    await this.#onProjectResolved(
      state.flow,
      { projectId: choice.id, projectName: choice.name, projectIdentifier: choice.identifier },
      state.answers
    );
  }

  /**
   * `projectIdentifier` n'est renseigné QUE quand le projet vient de la liste
   * complète (`project_pick`) : c'est lui qui devient `params.projectName` de
   * l'intent final, pour que `resolveProjectId` (côté handler) puisse le
   * résoudre même si l'exécution a lieu sur une page différente. Quand le
   * projet vient de la confirmation contextuelle ("Oui"), `projectName` reste
   * absent des params — `resolveProjectId` retombe alors sur
   * `voiceContext.currentProjectId()`, qui est justement le projet confirmé.
   */
  async #onProjectResolved(
    flow: QuickActionFlow,
    resolved: { projectId: string; projectName?: string; projectIdentifier?: string },
    answersBase: Record<string, unknown>
  ): Promise<void> {
    const label = this.#actionLabel(flow);
    this.voiceSession.suggestFlowTitle(resolved.projectName ? `${label} — ${resolved.projectName}` : label);

    const answers: Record<string, unknown> = { ...answersBase, ...resolved };
    delete answers['projectChoices'];

    if (flow === 'createTask') {
      this.#setState(flow, 'title', answers);
      this.#ask('Quel est le titre de la tâche ?');
      return;
    }

    // MOKA-207 Lot 4 : lecture seule, rien à demander de plus une fois le
    // projet résolu — le `projectIdentifier`/`projectId` résolus ci-dessus
    // suffisent à `#buildIntent`.
    if (flow === 'projectSummary') {
      await this.#executeFlow(flow, answers);
      return;
    }

    // `commentTask` (comme `assignTask`/`updateTaskStatus`/`deleteTask`) a
    // encore besoin de la tâche — passe par la liste commune ci-dessous,
    // la suite propre au flow est gérée dans `#handleTaskPick`.
    await this.#askTaskList(flow, answers);
  }

  // ─── Étape « tâche » (assignTask / updateTaskStatus / deleteTask) ─────

  async #askTaskList(flow: QuickActionFlow, answers: Record<string, unknown>): Promise<void> {
    const projectId = answers['projectId'] as string;
    const tasks = await firstValueFrom(this.taskService.getTasksByProject(projectId));

    if (tasks.length === 0) {
      this.#ask('Aucune tâche trouvée dans ce projet.');
      this.cancel();
      return;
    }

    // Les plus récentes d'abord : task_number croissant à la création, donc
    // tri décroissant dessus (fallback 0 si absent, ne devrait pas arriver).
    const recent = [...tasks]
      .sort((a, b) => (b.task_number ?? 0) - (a.task_number ?? 0))
      .slice(0, 10);

    const choices: TaskChoice[] = recent.map(t => ({ id: t.id!, title: t.title }));
    this.#setState(flow, 'task', { ...answers, taskChoices: choices });
    this.#ask(
      'Quelle tâche ?',
      recent.map(t => ({ id: t.id!, label: `#${t.task_number ?? '?'} ${t.title}` }))
    );
  }

  async #handleTaskPick(state: GuidedFlowState, option: VoiceMessageOption): Promise<void> {
    const choices = (state.answers['taskChoices'] as TaskChoice[] | undefined) ?? [];
    const choice = choices.find(c => c.id === option.id);

    if (!choice) {
      this.#ask('Tâche introuvable, réessayez.');
      this.cancel();
      return;
    }

    const answers: Record<string, unknown> = { ...state.answers, taskId: choice.id, taskTitle: choice.title };
    delete answers['taskChoices'];

    if (state.flow === 'assignTask') {
      await this.#askAssignee(state.flow, answers);
      return;
    }

    if (state.flow === 'updateTaskStatus') {
      await this.#askStatus(state.flow, answers);
      return;
    }

    if (state.flow === 'commentTask') {
      this.#setState(state.flow, 'comment', answers);
      this.#ask('Quel commentaire ajouter ?');
      return;
    }

    // deleteTask : dernière étape avant exécution, bloquante (action destructive).
    this.#setState(state.flow, 'confirm', answers);
    this.#ask(`Confirmez-vous la suppression de la tâche "${choice.title}" ?`, [
      { id: 'confirm', label: 'Confirmer la suppression' },
      { id: 'cancel', label: 'Annuler' }
    ]);
  }

  async #handleDeleteConfirm(state: GuidedFlowState, option: VoiceMessageOption): Promise<void> {
    if (option.id === 'cancel') {
      this.voiceSession.addMessage('assistant', 'Suppression annulée.', 'text');
      this.cancel();
      return;
    }

    await this.#executeFlow(state.flow, state.answers);
  }

  // ─── Étape « assigné » (assignTask) ───────────────────────────────────

  async #askAssignee(flow: QuickActionFlow, answers: Record<string, unknown>): Promise<void> {
    const projectId = answers['projectId'] as string;
    const members = await firstValueFrom(this.projectService.getProjectMembers(projectId));

    const options: VoiceMessageOption[] = [
      { id: 'self', label: 'Moi' },
      ...(members as ProjectMemberRow[])
        .filter(m => !!m.profile)
        .map(m => ({
          id: m.profile_id || m.profile?.id || '',
          label: `${m.profile?.first_name || ''} ${m.profile?.last_name || ''}`.trim()
        }))
    ];

    this.#setState(flow, 'assignee', answers);
    this.#ask('À qui assigner cette tâche ?', options);
  }

  async #handleAssigneePick(state: GuidedFlowState, option: VoiceMessageOption): Promise<void> {
    const assigneeName = option.id === 'self' ? 'moi' : option.label;
    await this.#executeFlow(state.flow, { ...state.answers, assigneeName });
  }

  // ─── Étape « statut » (updateTaskStatus) ──────────────────────────────

  async #askStatus(flow: QuickActionFlow, answers: Record<string, unknown>): Promise<void> {
    const projectId = answers['projectId'] as string;
    const statuses = await firstValueFrom(
      this.taskStatusService.getStatusesByProject(projectId, { enabledOnly: true })
    );

    // Seuls les statuts avec `system_key` sont sélectionnables : l'endpoint
    // `project.updateTaskStatus` valide `status` contre la whitelist des
    // system_key (cf. IntentValidatorService) — un statut custom sans
    // system_key n'est de toute façon pas atteignable par cette voie, même
    // hors flow guidé.
    const selectable = statuses.filter(s => !!s.system_key);

    if (selectable.length === 0) {
      this.#ask('Aucun statut sélectionnable sur ce projet.');
      this.cancel();
      return;
    }

    this.#setState(flow, 'status', answers);
    this.#ask(
      'Quel nouveau statut ?',
      selectable.map(s => ({ id: s.system_key as string, label: s.label }))
    );
  }

  async #handleStatusPick(state: GuidedFlowState, option: VoiceMessageOption): Promise<void> {
    await this.#executeFlow(state.flow, { ...state.answers, status: option.id });
  }

  // ─── addToCalendar (flow minimal assumé, cf. rapport de l'agent) ──────

  /**
   * Retour terrain, puis MOKA-407 : le calendrier se règle AVANT la moindre
   * question (« Aucun calendrier actif » à la toute fin était trompeur).
   * Calendrier ouvert ou unique : on enchaîne. Plusieurs et aucun d'ouvert :
   * boutons, dernier utilisé en premier. Aucun du tout : message d'aide.
   */
  async #beginAddToCalendar(flow: QuickActionFlow): Promise<void> {
    const askWhen = (answers: Record<string, unknown>) => {
      this.#setState(flow, 'when', answers);
      this.#ask('Pour quel jour ?', [
        { id: 'today', label: 'Aujourd\'hui' },
        { id: 'tomorrow', label: 'Demain' }
      ]);
    };

    if (this.calendarData.calendarActif?.id) {
      askWhen({});
      return;
    }
    const accessible = this.calendarChoice.accessibleCalendars();
    if (accessible.length === 0) {
      this.#ask('Aucun calendrier accessible. Rendez-vous sur la page Calendrier pour en configurer un.');
      return;
    }
    if (accessible.length === 1) {
      askWhen({ calendarId: accessible[0].id });
      return;
    }
    const choices = await this.calendarChoice.choices();
    this.#setState(flow, 'calendar_pick', {});
    this.#ask('Dans quel calendrier ?', choices.map(c => ({
      id: String(c.id),
      label: c.label,
      ...(c.isLast ? { emphasis: true, hint: LAST_CALENDAR_HINT } : {})
    })));
  }

  async #handleCalendarPick(state: GuidedFlowState, option: VoiceMessageOption): Promise<void> {
    const calendarId = Number(option.id);
    this.#setState(state.flow, 'when', { ...state.answers, calendarId });
    this.#ask('Pour quel jour ?', [
      { id: 'today', label: 'Aujourd\'hui' },
      { id: 'tomorrow', label: 'Demain' }
    ]);
  }

  async #handleWhenPick(state: GuidedFlowState, option: VoiceMessageOption): Promise<void> {
    const date = new Date();
    if (option.id === 'tomorrow') {
      date.setDate(date.getDate() + 1);
    }

    this.#setState(state.flow, 'portion', { ...state.answers, dateIso: this.#toIsoDate(date) });
    this.#ask('Quelle portion de la journée ?', [
      { id: 'fullday', label: 'Journée complète' },
      { id: 'morning', label: 'Matin' },
      { id: 'afternoon', label: 'Après-midi' }
    ]);
  }

  async #handlePortionPick(state: GuidedFlowState, option: VoiceMessageOption): Promise<void> {
    const portionMap: Record<string, CalendarPortion> = {
      fullday: CalendarPortion.FullDay,
      morning: CalendarPortion.Morning,
      afternoon: CalendarPortion.Afternoon
    };
    const portion = portionMap[option.id];
    await this.#executeFlow(state.flow, { ...state.answers, portion });
  }

  #toIsoDate(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // ─── myTasks (lecture seule) ───────────────────────────────────────────

  /** Dernière étape de `myTasks` : le scope choisi suffit à exécuter (GET, aucune confirmation). */
  async #handleScopePick(state: GuidedFlowState, option: VoiceMessageOption): Promise<void> {
    await this.#executeFlow(state.flow, { ...state.answers, scope: option.id });
  }

  // ─── reserveRoom (flow minimal, même logique que addToCalendar sans portion) ──

  /**
   * Dernière étape de `reserveRoom` : `date` est le SEUL champ requis par
   * `rooms.reserveRoom` (cf. `IntentValidatorService`) — `startTime`/
   * `endTime`/`roomName` restent volontairement absents (jamais inventés,
   * même règle que le prompt GPT du module Salles), donc rien à demander de
   * plus une fois le jour choisi.
   */
  async #handleRoomWhenPick(state: GuidedFlowState, option: VoiceMessageOption): Promise<void> {
    const date = new Date();
    if (option.id === 'tomorrow') {
      date.setDate(date.getDate() + 1);
    }
    await this.#executeFlow(state.flow, { ...state.answers, dateIso: this.#toIsoDate(date) });
  }

  // ─── Contexte conversationnel pré-rempli (MOKA-207 Lot 3) ─────────────

  /**
   * Pré-remplissage des défauts de contexte pour une création (`flow` dont
   * l'id commence par `create`) — cf. JSDoc de `start()`. Générique : lit
   * `entities.person`/`group`/`release`/`dueDate` (posées par
   * `session.setContext`, cf. `SessionVoiceHandler`) et les convertit en
   * `ContextDefault[]` mémorisés dans `answers['__contextDefaults']`, sans
   * connaître les flows individuels — `#executeFlow` se charge de les
   * fusionner dans `intent.params` au moment de l'exécution, quel que soit
   * le flow.
   */
  #buildInitialAnswers(flow: QuickActionFlow): Record<string, unknown> {
    if (!flow.startsWith('create')) return {};

    const defaults = this.#collectContextDefaults();
    return defaults.length > 0 ? { __contextDefaults: defaults } : {};
  }

  #collectContextDefaults(): ContextDefault[] {
    const entities = this.voiceSession.entities();
    const defaults: ContextDefault[] = [];

    const pushEntity = (paramKey: string, label: string, entity: SessionContextEntity | undefined) => {
      if (entity?.name) {
        defaults.push({ paramKey, label, value: entity.name });
      }
    };

    pushEntity('assigneeName', 'Assigné à', entities.person);
    pushEntity('groupName', 'Sprint / groupe', entities.group);
    pushEntity('releaseName', 'Release', entities.release);

    if (entities.dueDate) {
      defaults.push({ paramKey: 'dueDate', label: 'Échéance', value: entities.dueDate });
    }

    return defaults;
  }

  /**
   * Étape générique de confirmation du contexte pré-rempli (garde-fou MOKA-207
   * Lot 3, non négociable) : "Modifier" annule la création sans rien exécuter
   * — l'utilisateur ajuste le contexte via les chips (`VoiceContextChipsComponent`)
   * puis relance l'action ; "Créer" rejoue `#executeFlow` avec
   * `__contextConfirmed: true`, qui court-circuite alors le récap (cf.
   * `#executeFlow`) et exécute pour de vrai.
   */
  async #handleContextRecapConfirm(state: GuidedFlowState, option: VoiceMessageOption): Promise<void> {
    if (option.id === 'modify') {
      this.voiceSession.addMessage(
        'assistant',
        'Création annulée. Ajustez le contexte pré-rempli via les chips puis relancez l\'action.',
        'text'
      );
      this.cancel();
      return;
    }

    await this.#executeFlow(state.flow, state.answers);
  }

  // ─── Exécution finale ──────────────────────────────────────────────────

  /**
   * Point d'exécution unique de tout flow guidé. MOKA-207 Lot 3 : construit
   * l'intent d'abord, puis ne retient des défauts de contexte injectés
   * (`#buildInitialAnswers`) que ceux réellement consommés par
   * `intent.endpoint` (cf. `#filterSupportedDefaults` — sans ce filtre, le
   * récap promettrait un contexte que le handler ignore silencieusement, ex.
   * "Sprint : X" pour `project.createProject`). Si au moins un défaut
   * pertinent subsiste et n'a pas encore été confirmé, s'arrête sur un
   * récapitulatif (étape générique `context_recap`, boutons Créer/Modifier)
   * AVANT toute exécution — jamais seulement une mention après coup. Sans
   * défaut pertinent (aucun contexte injecté, ou endpoint qui n'en consomme
   * aucun), exécution directe inchangée. Étape générique du MOTEUR de flow
   * (pas par flow individuel) : s'applique automatiquement à tout flow de
   * création présent ou futur, sans édition de `#buildIntent` ni des
   * définitions de flow existantes.
   */
  async #executeFlow(flow: QuickActionFlow, answers: Record<string, unknown>): Promise<void> {
    const intent = this.#buildIntent(flow, answers);

    const rawDefaults = (answers['__contextDefaults'] as ContextDefault[] | undefined) ?? [];
    const defaults = this.#filterSupportedDefaults(intent.endpoint, rawDefaults);

    if (defaults.length > 0 && !answers['__contextConfirmed']) {
      this.#setState(flow, 'context_recap', { ...answers, __contextConfirmed: true });
      const summary = defaults.map(d => `${d.label} : ${d.value}`).join(' · ');
      this.#ask(`Récapitulatif avant création — ${summary}. Confirmez-vous ?`, [
        { id: 'confirm', label: 'Créer' },
        { id: 'modify', label: 'Modifier' }
      ]);
      return;
    }

    for (const d of defaults) {
      if (intent.params[d.paramKey] === undefined) {
        intent.params[d.paramKey] = d.value;
      }
    }

    this.#activeFlow.set(null);
    this.telemetry.track({ eventType: 'flow_completed', endpoint: flow, inputMode: 'text' });
    await this.voiceService.executeIntent(intent, 'text');
  }

  /**
   * Ne garde que les défauts de contexte dont le paramètre est listé pour
   * `endpoint` dans `CONTEXT_DEFAULT_SUPPORTED_PARAMS` — cf. sa JSDoc pour le
   * raisonnement. Aucune entrée pour `endpoint` (ex. `project.createProject`)
   * → tableau vide, aucun récap.
   */
  #filterSupportedDefaults(endpoint: string, defaults: ContextDefault[]): ContextDefault[] {
    const supported = CONTEXT_DEFAULT_SUPPORTED_PARAMS[endpoint] ?? [];
    return defaults.filter(d => supported.includes(d.paramKey));
  }

  /**
   * Construit un `VoiceIntent` complet (tous les champs renseignés, y compris
   * ceux non pertinents ici — `confirmationRequired`/`responseMessage`/
   * `confidence`/`needsClarification`/`clarificationQuestion`) pour rester
   * correctement typé et ne jamais déclencher `IntentValidatorService` sur
   * `needsClarification` par erreur.
   */
  #buildIntent(flow: QuickActionFlow, answers: Record<string, unknown>): VoiceIntent {
    const base = {
      confirmationRequired: false,
      responseMessage: '',
      confidence: 1,
      needsClarification: false,
      clarificationQuestion: null
    } as const;

    switch (flow) {
      case 'createTask': {
        const projectIdentifier = answers['projectIdentifier'] as string | undefined;
        return {
          ...base,
          method: 'POST',
          endpoint: 'project.createTask',
          params: {
            title: answers['title'],
            ...(projectIdentifier ? { projectName: projectIdentifier } : {})
          }
        };
      }
      case 'createProject':
        return {
          ...base,
          method: 'POST',
          endpoint: 'project.createProject',
          params: { name: answers['name'] }
        };
      case 'assignTask':
        return {
          ...base,
          method: 'POST',
          endpoint: 'project.assignTask',
          params: {
            taskId: answers['taskId'],
            taskTitle: answers['taskTitle'],
            assigneeName: answers['assigneeName']
          }
        };
      case 'updateTaskStatus':
        return {
          ...base,
          method: 'POST',
          endpoint: 'project.updateTaskStatus',
          params: {
            taskId: answers['taskId'],
            taskTitle: answers['taskTitle'],
            status: answers['status']
          }
        };
      case 'deleteTask':
        return {
          ...base,
          method: 'DELETE',
          endpoint: 'project.deleteTask',
          params: {
            taskId: answers['taskId'],
            taskTitle: answers['taskTitle']
          }
        };
      case 'addToCalendar':
        return {
          ...base,
          method: 'POST',
          endpoint: 'calendar.addPresences',
          params: {
            dates: [answers['dateIso']],
            portion: answers['portion'],
            ...(answers['calendarId'] ? { calendarId: answers['calendarId'] } : {})
          }
        };
      case 'myTasks':
        return {
          ...base,
          method: 'GET',
          endpoint: 'project.listMyTasks',
          params: { scope: answers['scope'] }
        };
      case 'projectSummary': {
        const projectIdentifier = answers['projectIdentifier'] as string | undefined;
        return {
          ...base,
          method: 'GET',
          endpoint: 'project.projectSummary',
          params: projectIdentifier ? { projectName: projectIdentifier } : {}
        };
      }
      case 'commentTask':
        return {
          ...base,
          method: 'POST',
          endpoint: 'project.addComment',
          params: {
            taskId: answers['taskId'],
            taskTitle: answers['taskTitle'],
            comment: answers['comment']
          }
        };
      case 'reserveRoom':
        return {
          ...base,
          method: 'POST',
          endpoint: 'rooms.reserveRoom',
          params: { date: answers['dateIso'] }
        };
      case 'reportClaim':
        return {
          ...base,
          method: 'POST',
          endpoint: 'inventory.createClaim',
          params: { description: answers['description'] }
        };
      default: {
        const exhaustive: never = flow;
        throw new Error(`Flow non supporté: ${exhaustive}`);
      }
    }
  }

  #setState(flow: QuickActionFlow, step: string, answers: Record<string, unknown>): void {
    this.#activeFlow.set({ flow, step, answers });
  }

  /** Label lisible d'un flow (cf. `QUICK_ACTIONS`), fallback sur l'id technique si absent (ne devrait jamais arriver) */
  #actionLabel(flow: QuickActionFlow): string {
    return QUICK_ACTIONS.find(a => a.id === flow)?.label ?? flow;
  }

  #ask(question: string, options?: VoiceMessageOption[]): void {
    this.voiceSession.addMessage('assistant', question, 'text', options);
  }
}
